/* eslint-disable jsx-a11y/prefer-tag-over-role */
import { useQueries } from '@tanstack/react-query';
import React, { memo, useCallback, useMemo } from 'react';
import { Virtual } from 'swiper/modules';
import getCommonLayout from '@/lib/Layouts/commonLayouts';
import { HomePageProps } from '@/types/props/pages';
import IndexMeta from '@/components/seo/meta/IndexMeta';
import Container from '@/components/Container';
import strapiConstants from '@/constants/strapi.constants';
import {
  createMostPopularTopicUrl,
  createMostWatchedTopicUrl,
  createNewestTopicUrl,
  createReleaseCalendarTopicUrl,
} from '@/utilites/algolia/create-algolia-urls';
import useBackendUser from '@/hooks/useBackendUser';
import checkArray from '@/utilites/check-array';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { IndexEnum } from '@/types/translations/IndexEnum';
import { CommonEnum } from '@/types/translations/CommonEnum';
// API imports
import getLastActivities from '@/api/getLastActivities';
import getTopSellers from '@/api/getTopSellers';
import getMostPopular from '@/api/getMostPopular';
import getBottleOfTheDay from '@/api/getBottleOfTheDay';
import getRecommendedSkus from '@/api/getRecommendedSkus';
import getNewSkus from '@/api/getNewSkus';
import getMostWatched from '@/api/getMostWatched';
import getBlogPostsUseQuery from '@/api/getBlogPostsUseQuery';
import getCollectionsUseQuery from '@/api/getCollectionsUseQuery';
import { SkuDTO } from '@/types/api/sku';
import CategoryNav from '@/components/home/categoryNav';
import getIndexStaticSideProps from '@/lib/staticSideProps/getIndexStaticSideProps';
import LazyLoadOnView from '@/components/common/LazyLoadOnView';
import IndexSkusCarousel from '@/components/home/Index/IndexSkusCarousel';
import IndexBannerSlider from '@/components/home/Index/IndexBannerSlider';
import IndexBrandsSlider from '@/components/home/Index/IndexBrandsSlider';
import IndexActivities from '@/components/home/Index/IndexActivities';
import IndexCollectionsCarousel from '@/components/home/Index/IndexCollectionsCarousel';
import IndexPopularBrands from '@/components/home/Index/IndexPopularBrands';
import IndexBottleOfTheDay from '@/components/home/Index/IndexBottleOfTheDay';
import IndexWhyBuyOnSpiritory from '@/components/home/Index/IndexWhyBuyOnSpiritory';
import IndexBlogPostsCarousel from '@/components/home/Index/IndexLatestBlogPosts';
import IndexDownloadTheApp from '@/components/home/Index/IndexDownloadTheApp';
import getReleaseCalendar from '@/api/getReleaseCalendar';
import getMostPopularAroundYou from '@/api/getMostPopularAroundYou';
import IndexDiscoverSpiritory from '@/components/home/Index/IndexDiscoverSpiritory';
import IndexNav from '@/components/home/Index/IndexNav/IndexNav';
import Reviews from '@/components/home/reviews';
// Simplified error boundary with better typing
class ErrorBoundary extends React.Component<
  { fallback?: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { fallback?: React.ReactNode; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error in component:', error, errorInfo);
  }

  render() {
    return this.state.hasError
      ? this.props.fallback || <div>Something went wrong. Please try again later.</div>
      : this.props.children;
  }
}

// Helper function for preparing query data
const prepareQueryData = <T,>(data: any): T[] => {
  return (checkArray(data) ? data : []) as T[];
};

// Shared React Query config to reduce repetition
const QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};

const HomePage = ({ locale, user: userSSR, browserLocale }: HomePageProps) => {
  const { translate } = useTypedTranslation<IndexEnum>('index');
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');

  const selectSkus = useCallback((data: any) => prepareQueryData<SkuDTO>(data), []);

  // Consolidated React Query configuration
  const [
    lastActivitiesQuery,
    topSellersQuery,
    mostPopularQuery,
    bottleOfTheDayQuery,
    recommendedSkusQuery,
    collectionsQuery,
    newSkusQuery,
    mostWatchedQuery,
    blogPostsQuery,
    releaseCalendarQuery,
    mostPopularAroundYouQuery,
  ] = useQueries({
    queries: [
      {
        ...QUERY_CONFIG,
        queryKey: ['lastActivities'],
        queryFn: getLastActivities,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['topSellers'],
        queryFn: getTopSellers,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['mostPopular'],
        queryFn: getMostPopular,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['bottleOfTheDay'],
        queryFn: getBottleOfTheDay,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['recommendedSkus'],
        queryFn: getRecommendedSkus,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['collections', strapiConstants.queries.collection, locale],
        queryFn: getCollectionsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['newSkus'],
        queryFn: getNewSkus,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['mostWatched'],
        queryFn: getMostWatched,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['blogs', strapiConstants.queries.blogHome, locale],
        queryFn: getBlogPostsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['releaseCalendar'],
        queryFn: getReleaseCalendar,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['mostPopularAroundYou'],
        queryFn: getMostPopularAroundYou,
        select: selectSkus,
      },
    ],
  });

  const { data: user } = useBackendUser();
  const userId = useMemo(() => user?.id || null, [user]);

  const newCollections = useMemo(() => {
    const collections = collectionsQuery.data || [];
    const _newCollections = collections.sort((a, b) => {
      const dateA = new Date(a.attributes?.createdAt || '');
      const dateB = new Date(b.attributes?.createdAt || '');
      return dateB.getTime() - dateA.getTime();
    });
    const newCollections = _newCollections.slice(0, 5);
    return newCollections.map(collection => ({
      name: collection?.attributes?.name || '',
      href: `/collections/${collection.attributes?.slug}`,
    }));
  }, [collectionsQuery.data]);

  const newSkus = useMemo(() => {
    const skus = newSkusQuery.data || [];
    const _newSkus = skus.slice(0, 5).map(sku => ({
      name: sku?.name || '',
      href: `/${sku.slug}`,
    }));
    return _newSkus;
  }, [newSkusQuery.data]);

  // Simplified carousel rendering function
  const renderSkuCarousel = useCallback(
    (skus: SkuDTO[], title: string, href?: string, isLoading = false, isRelease = false) => (
      <div className="tw-mb-16">
        <IndexSkusCarousel
          skus={skus}
          href={href}
          title={title}
          isLoading={isLoading}
          isRelease={isRelease}
          modules={[Virtual]}
          virtual={{
            enabled: true,
            addSlidesBefore: 2,
            addSlidesAfter: 2,
            cache: true,
          }}
        />
      </div>
    ),
    []
  );

  return (
    <>
      <IndexMeta />

      {/* Main content with rendering optimization attributes */}
      <Container className="tw-flex tw-flex-col max-nine:tw-px-0">
        <div className="max-nine:tw-hidden">
          <IndexNav newSkus={newSkus} newCollections={newCollections} />
        </div>
        <div className="nine:tw-hidden">
          <CategoryNav userId={userId} />
        </div>

        {/* Main banner - critical for first paint */}
        <div
          className="tw-mb-3 max-nine:tw-mb-3"
          style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}
        >
          <IndexBannerSlider userId={userId} />
        </div>

        {/* Brands - critical for first paint */}
        <div
          className="tw-mb-7 max-nine:tw-mb-9 max-nine:tw-px-4"
          style={{ containIntrinsicSize: '0 6rem', contentVisibility: 'auto' }}
        >
          <IndexBrandsSlider />
        </div>

        {/* Recent activities - consolidated error boundary */}
        <ErrorBoundary>
          <div
            className="tw-mb-16 max-nine:tw-mb-8 max-nine:tw-px-4"
            style={{
              containIntrinsicSize: '0 24rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexActivities
              lastActivities={lastActivitiesQuery.data || []}
              topSellers={topSellersQuery.data || []}
              isLoading={lastActivitiesQuery.isLoading || topSellersQuery.isLoading}
            />
          </div>
        </ErrorBoundary>

        {/* Most popular */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            mostPopularQuery.data || [],
            translateCommon(CommonEnum['most_popular']),
            createMostPopularTopicUrl(),
            mostPopularQuery.isLoading
          )}
        </div>

        {/* Recommended */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            recommendedSkusQuery.data || [],
            translate(IndexEnum['recommended_by_spiritory']),
            undefined,
            recommendedSkusQuery.isLoading
          )}
        </div>

        {/* Collections - uses LazyLoadOnView for deferred loading */}
        <LazyLoadOnView height="24rem">
          <div
            className="tw-mb-16 max-nine:tw-mb-5"
            style={{
              containIntrinsicSize: '0 24rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexCollectionsCarousel
              collections={collectionsQuery.data || []}
              isLoading={collectionsQuery.isLoading}
            />
          </div>
        </LazyLoadOnView>

        {/* Popular brands - uses LazyLoadOnView for deferred loading */}
        <LazyLoadOnView height="20rem">
          <div
            className="tw-mb-16"
            style={{
              containIntrinsicSize: '0 20rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexPopularBrands />
          </div>
        </LazyLoadOnView>

        {/* Most popular around you */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            mostPopularAroundYouQuery.data || [],
            translateCommon(CommonEnum['most_popular_around_you']),
            undefined,
            mostPopularAroundYouQuery.isLoading
          )}
        </div>
      </Container>

      {/* Bottle of the day - outside main container */}
      <LazyLoadOnView height="32rem">
        <div
          className="tw-mb-16"
          style={{ containIntrinsicSize: '0 32rem', contentVisibility: 'auto' }}
        >
          <IndexBottleOfTheDay
            sku={bottleOfTheDayQuery.data || null}
            isLoading={bottleOfTheDayQuery.isLoading}
          />
        </div>
      </LazyLoadOnView>

      <Container className="max-nine:tw-px-0">
        {/* Discover Spiritory
        <LazyLoadOnView height="20rem">
          <div
            className="tw-mb-14"
            style={{
              containIntrinsicSize: '0 20rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexDiscoverSpiritory />
          </div>
        </LazyLoadOnView>
        */}

        {/* Most watched */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            mostWatchedQuery.data || [],
            translateCommon(CommonEnum['most_watched']),
            createMostWatchedTopicUrl(),
            mostWatchedQuery.isLoading
          )}
        </div>

        {/* Why buy on Spiritory */}
        <LazyLoadOnView height="20rem">
          <div
            className="tw-mb-14"
            style={{
              containIntrinsicSize: '0 20rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexWhyBuyOnSpiritory user={user} />
          </div>
        </LazyLoadOnView>

        {/* Recently added */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            newSkusQuery.data || [],
            translateCommon(CommonEnum['new']),
            createNewestTopicUrl(),
            newSkusQuery.isLoading
          )}
        </div>

        {/* Release calendar */}
        <div style={{ containIntrinsicSize: '0 24rem', contentVisibility: 'auto' }}>
          {renderSkuCarousel(
            releaseCalendarQuery.data || [],
            translateCommon(CommonEnum['release_calendar']),
            createReleaseCalendarTopicUrl(),
            releaseCalendarQuery.isLoading,
            true
          )}
        </div>

        {/* Reviews */}
        <Reviews></Reviews>

        {/* Blog posts */}
        <LazyLoadOnView height="24rem">
          <div
            className="tw-mb-16 max-nine:tw-mb-8"
            style={{
              containIntrinsicSize: '0 24rem',
              contentVisibility: 'auto',
            }}
          >
            <IndexBlogPostsCarousel
              blogPosts={blogPostsQuery.data || []}
              browserLocale={browserLocale}
              isLoading={blogPostsQuery.isLoading}
            />
          </div>
        </LazyLoadOnView>

        {/* App download */}
        <div
          className="tw-mb-16"
          style={{ containIntrinsicSize: '0 10rem', contentVisibility: 'auto' }}
        >
          <IndexDownloadTheApp userId={userId} />
        </div>
      </Container>
    </>
  );
};

export const getStaticProps = getIndexStaticSideProps;
HomePage.getLayout = getCommonLayout('index');
export default memo(HomePage);
